import React from 'react';
import { Checkbox } from '../Checkbox';
import { Icon } from '../Icon';
import styles from './FilterDrawer.module.css';

/**
 * Collapsible multi-select filter side-panel for DataTable pages.
 *
 * Renders to the LEFT of the table card. When `isOpen` is false the drawer
 * collapses to width 0 with a smooth transition (still in the DOM so the table
 * animates as the drawer slides in/out).
 *
 * 2.0 design system: each option renders the shared <Checkbox>; chrome uses
 * <Icon>. Filter contract (multi-select) is unchanged:
 *   { key, label, values: string[], onChange: (next[]) => void,
 *     options: [{ value, label, count }] }
 *
 * Props:
 *   isOpen   — boolean
 *   onClose  — fired by the close button
 *   filters  — array of filter configs (see above)
 */
const FilterDrawer = ({ isOpen, onClose, filters = [] }) => {
  const totalActive = filters.reduce(
    (sum, f) => sum + (Array.isArray(f.values) ? f.values.length : 0),
    0
  );

  const clearAll = () => {
    filters.forEach((f) => {
      if (Array.isArray(f.values) && f.values.length > 0) f.onChange?.([]);
    });
  };

  const toggleValue = (filter, value) => {
    const current = Array.isArray(filter.values) ? filter.values : [];
    const next = current.includes(value)
      ? current.filter((v) => v !== value)
      : [...current, value];
    filter.onChange?.(next);
  };

  const removeValue = (filter, value) => {
    const current = Array.isArray(filter.values) ? filter.values : [];
    filter.onChange?.(current.filter((v) => v !== value));
  };

  const renderChips = () => {
    const chips = [];
    filters.forEach((filter) => {
      (Array.isArray(filter.values) ? filter.values : []).forEach((v) => {
        const opt = filter.options?.find((o) => o.value === v);
        const optLabel = opt?.label ?? String(v);
        chips.push(
          <button
            key={`${filter.key}:${v}`}
            type="button"
            className={styles.chip}
            onClick={() => removeValue(filter, v)}
            title={`Remove ${filter.label}: ${optLabel}`}
          >
            <span className={styles.chipLabel}>
              <span className={styles.chipFilterLabel}>{filter.label}:</span> {optLabel}
            </span>
            <Icon name="close" size="xs" />
          </button>
        );
      });
    });
    return chips;
  };

  return (
    <aside className={`${styles.drawer} ${isOpen ? styles.open : ''}`} aria-hidden={!isOpen}>
      {isOpen && (
        <>
          <div className={styles.header}>
            <span className={styles.title}>Filter by</span>
            <div className={styles.headerActions}>
              {totalActive > 0 && (
                <button type="button" className={styles.clearAllLink} onClick={clearAll}>
                  Clear filters
                </button>
              )}
              <button
                type="button"
                className={styles.closeButton}
                onClick={onClose}
                aria-label="Close filter drawer"
                title="Close filter drawer"
              >
                <Icon name="chevron-left" />
              </button>
            </div>
          </div>

          <div className={styles.body}>
            {totalActive > 0 && <div className={styles.chipStrip}>{renderChips()}</div>}

            {filters.length === 0 ? (
              <div className={styles.empty}>No filters available</div>
            ) : (
              filters.map((filter) => {
                const values = Array.isArray(filter.values) ? filter.values : [];
                return (
                  <div key={filter.key} className={styles.section}>
                    <div className={styles.sectionHeader}>
                      <span className={styles.sectionTitle}>{filter.label}</span>
                    </div>
                    <ul className={styles.options}>
                      {filter.options?.map((option) => {
                        const isSelected = values.includes(option.value);
                        return (
                          <li key={String(option.value)} className={styles.option}>
                            <Checkbox
                              checked={isSelected}
                              onCheckedChange={() => toggleValue(filter, option.value)}
                              aria-label={option.label}
                            />
                            <span
                              className={`${styles.optionLabel} ${isSelected ? styles.optionLabelSelected : ''}`}
                              onClick={() => toggleValue(filter, option.value)}
                            >
                              {option.label}
                            </span>
                            {typeof option.count === 'number' && (
                              <span className={styles.optionCount}>({option.count})</span>
                            )}
                          </li>
                        );
                      })}
                    </ul>
                  </div>
                );
              })
            )}
          </div>
        </>
      )}
    </aside>
  );
};

export default FilterDrawer;
